import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Plus } from 'lucide-react'

import { useDebounce } from '@/components/hooks/use-debounce'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency } from '@/lib/format'
import {
  ConsignmentForm,
  type ConsignmentFormValues,
} from '@/features/consignment/consignment-form'
import { useConsignments, useCreateConsignment } from '@/features/consignment/hooks'
import {
  boatName,
  consignorName,
  CONSIGNMENT_STATUSES,
  CONSIGNMENT_STATUS_LABELS,
  CONSIGNMENT_STATUS_VARIANT,
  type Consignment,
  type ConsignmentStatus,
} from '@/types/consignment'

// Stable identity for the loading state: TanStack Table reruns its row models
// (and resets pagination state) whenever `data` changes identity, so a fresh
// `[]` on every render would loop forever while the query is in flight.
const EMPTY_ROWS: Consignment[] = []

const columns: ColumnDef<Consignment>[] = [
  {
    id: 'boat',
    header: 'Boat',
    cell: ({ row }) => {
      const c = row.original
      return (
        <Link
          to="/inventory/$boatId"
          params={{ boatId: c.boat_id }}
          className="font-medium hover:underline"
        >
          {boatName(c)}
          {c.boat && <span className="block text-xs text-muted-foreground">{c.boat.year}</span>}
        </Link>
      )
    },
  },
  {
    id: 'consignor',
    header: 'Consignor',
    cell: ({ row }) => {
      const c = row.original
      return c.consignor ? (
        <Link
          to="/sales/$customerId"
          params={{ customerId: c.consignor_id }}
          className="hover:underline"
        >
          {consignorName(c)}
        </Link>
      ) : (
        <span className="text-muted-foreground">—</span>
      )
    },
  },
  {
    accessorKey: 'asking_price',
    header: 'Asking',
    cell: ({ row }) => <span>{formatCurrency(row.original.asking_price)}</span>,
  },
  {
    accessorKey: 'commission_rate',
    header: 'Rate',
    cell: ({ row }) => <span>{row.original.commission_rate}%</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant="outline" className={CONSIGNMENT_STATUS_VARIANT[row.original.status]}>
        {CONSIGNMENT_STATUS_LABELS[row.original.status]}
      </Badge>
    ),
  },
  {
    accessorKey: 'sale_price',
    header: 'Sale',
    cell: ({ row }) =>
      row.original.sale_price != null ? (
        <span>{formatCurrency(row.original.sale_price)}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'net_proceeds',
    header: 'Owner Proceeds',
    cell: ({ row }) =>
      row.original.net_proceeds != null ? (
        <span>{formatCurrency(row.original.net_proceeds)}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <Link to="/inventory/consignment/$consignmentId" params={{ consignmentId: row.original.id }}>
        <Button variant="outline" size="sm">
          View
        </Button>
      </Link>
    ),
  },
]

// Consignment slice of the Inventory page: the agreements list with its own
// search/filter state (the boats tab owns the URL's q param) and create dialog.
export function ConsignmentsTab() {
  const [status, setStatus] = useState<ConsignmentStatus | 'all'>('all')
  const [searchInput, setSearchInput] = useState('')
  const debouncedQ = useDebounce(searchInput, 300)

  const { data, isLoading, isError } = useConsignments({
    status: status === 'all' ? undefined : status,
    q: debouncedQ || undefined,
  })

  const [createOpen, setCreateOpen] = useState(false)
  const createMutation = useCreateConsignment()

  const table = useReactTable({
    data: data ?? EMPTY_ROWS,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleCreate = async (values: ConsignmentFormValues) => {
    try {
      await createMutation.mutateAsync({
        boat_id: values.boat_id,
        consignor_id: values.consignor_id,
        commission_rate: values.commission_rate,
        asking_price: values.asking_price,
        agreement_end: values.agreement_end || undefined,
        notes: values.notes || '',
      })
      setCreateOpen(false)
    } catch {
      // createMutation.isError surfaces the message in the dialog.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            placeholder="Search boat or owner…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full sm:max-w-sm"
          />
          <Select value={status} onValueChange={(v) => setStatus(v as ConsignmentStatus | 'all')}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {CONSIGNMENT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {CONSIGNMENT_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Consignment
          </Button>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Consign a boat</DialogTitle>
              <DialogDescription>
                Sign a consignment agreement: you list the owner's boat and keep an agreed percentage
                of the sale price.
              </DialogDescription>
            </DialogHeader>
            <ConsignmentForm
              onSubmit={handleCreate}
              isPending={createMutation.isPending}
              submitLabel="Create Agreement"
            />
            {createMutation.isError && (
              <p className="text-sm text-destructive">
                Failed to create consignment: {createMutation.error.message}
              </p>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <Skeleton className="h-[400px] w-full" />
      ) : isError ? (
        <div className="text-destructive">Failed to load consignments.</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No consignments found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
